import React, { useState, useEffect, useCallback, useRef } from 'react';
import { fetchCommunities } from '../services/dashboardService';

// Deterministic colour from community UUID
const getCommunityColor = (id) => {
  if (!id) return '#6366f1';
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = id.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 60%, 50%)`;
};

const getRatingLabel = (rating) => {
  if (rating >= 0.7) return { label: 'High', cls: 'rating-high' };
  if (rating >= 0.4) return { label: 'Medium', cls: 'rating-med' };
  return { label: 'Low', cls: 'rating-low' };
};

const CommunityCard = ({ community, onClick }) => {
  const [expanded, setExpanded] = useState(false);
  const { id, title, summary, rating, node_count } = community;
  const color = getCommunityColor(id);
  const { label, cls } = getRatingLabel(rating ?? 0);

  return (
    <div className="community-card" onClick={() => onClick(community)} style={{ borderTop: `4px solid ${color}` }}>
      <div className="community-card-header">
        <h3 className="community-card-title">{title || 'Untitled Community'}</h3>
        <span className={`community-rating-badge ${cls}`}>{label}</span>
      </div>

      <div className="community-rating-bar-wrap">
        <div
          className="community-rating-bar-fill"
          style={{ width: `${(rating ?? 0) * 100}%`, background: color }}
        />
      </div>

      <p className={`community-card-summary ${expanded ? 'expanded' : ''}`}>
        {summary || 'No summary available.'}
      </p>
      {summary && summary.length > 180 && (
        <button
          className="community-read-more"
          onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
        >
          {expanded ? 'Show less' : 'Read more'}
        </button>
      )}

      <div className="community-card-footer">
        <span className="community-node-count">
          <i className="fas fa-circle-nodes"></i> {node_count ?? 0} entities
        </span>
        <span className="community-explore-hint">
          Explore <i className="fas fa-arrow-right"></i>
        </span>
      </div>
    </div>
  );
};

const SkeletonCard = () => (
  <div className="community-card community-card-skeleton">
    <div className="skeleton-line skeleton-title" />
    <div className="skeleton-line skeleton-bar" />
    <div className="skeleton-line" />
    <div className="skeleton-line" />
    <div className="skeleton-line skeleton-short" />
  </div>
);

const CommunityGrid = ({ onSelectCommunity }) => {
  const [communities, setCommunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'list'
  const debounceRef = useRef(null);

  const loadCommunities = useCallback(async (search = '') => {
    setLoading(true);
    setError(null);
    try {
      // Pass search directly - empty string means no filter, API returns all communities
      const result = await fetchCommunities(search.trim());
      if (result.success) {
        const sorted = [...(result.data || [])].sort((a, b) => (b.rating ?? 0) - (a.rating ?? 0));
        setCommunities(sorted);
      } else {
        setError(result.message || 'Failed to load communities.');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCommunities();
  }, [loadCommunities]);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchQuery(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      loadCommunities(val);
    }, 300);
  };

  const handleCardClick = (community) => {
    if (onSelectCommunity) onSelectCommunity(community);
  };

  const renderEmpty = () => (
    <div className="community-empty-state">
      {searchQuery ? (
        <>
          <i className="fas fa-search community-empty-icon"></i>
          <h3>No communities matched your search</h3>
          <p>Try broadening your query or explore all communities.</p>
          <button className="btn-secondary" onClick={() => { setSearchQuery(''); loadCommunities(''); }}>
            Clear Search
          </button>
        </>
      ) : (
        <>
          <i className="fas fa-diagram-project community-empty-icon"></i>
          <h3>Your knowledge graph is empty</h3>
          <p>Upload and process PDF documents to begin extracting knowledge.</p>
        </>
      )}
    </div>
  );

  return (
    <div className="community-grid-page">
      <div className="community-grid-toolbar">
        <div className="community-search-wrap">
          <i className="fas fa-search community-search-icon"></i>
          <input
            type="text"
            className="community-search-input"
            placeholder="Search communities by topic or keyword..."
            value={searchQuery}
            onChange={handleSearchChange}
          />
          {searchQuery && (
            <button className="community-search-clear" onClick={() => { setSearchQuery(''); loadCommunities(''); }}>
              <i className="fas fa-times"></i>
            </button>
          )}
        </div>
        <div className="community-view-toggle">
          <button
            className={`view-toggle-btn ${viewMode === 'grid' ? 'active' : ''}`}
            onClick={() => setViewMode('grid')}
            title="Grid View"
          >
            <i className="fas fa-th-large"></i>
          </button>
          <button
            className={`view-toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
            onClick={() => setViewMode('list')}
            title="List View"
          >
            <i className="fas fa-list"></i>
          </button>
        </div>
      </div>

      {error && (
        <div className="community-error-state">
          <i className="fas fa-exclamation-triangle"></i> {error}
          <button className="btn-secondary" onClick={() => loadCommunities(searchQuery)} style={{ marginLeft: 12 }}>
            Retry
          </button>
        </div>
      )}

      {loading ? (
        <div className={`community-grid ${viewMode === 'list' ? 'community-list-mode' : ''}`}>
          {Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)}
        </div>
      ) : communities.length === 0 ? (
        renderEmpty()
      ) : (
        <div className={`community-grid ${viewMode === 'list' ? 'community-list-mode' : ''}`}>
          {communities.map(c => (
            <CommunityCard key={c.id} community={c} onClick={handleCardClick} />
          ))}
        </div>
      )}
    </div>
  );
};

export default CommunityGrid;
